/**
 * TreasureAssignmentPanel — which reward sits on which planet.
 *
 * Planets are grouped by their derived round (the same topology depth the map
 * builder badges as R1/R2/…), so an admin can see at a glance how the roll
 * spread the pool across the game's arc.
 *
 * "Re-roll" re-runs `distributeTrlTreasure` over the CURRENT map and replaces
 * every assignment; individual planets can then be adjusted by hand. Nothing
 * here writes to the server — changes flow up to TrlMapBuilder, which owns node
 * state and persists them with the map's own SAVE.
 */

import { useMemo } from 'react';
import {
  distributeTrlTreasure,
  describeTrlTreasureItem,
  TRL_TREASURE_TIER_LABELS,
  type TrlMapNodeDef,
  type TrlTreasureConfig,
} from '@swtorbit/shared-socket-events';
import { usePlatformItemSummaries } from '../../../../hooks/usePlatformItems';
import { ItemIcon } from './ItemIcon';

interface TreasureAssignmentPanelProps {
  nodes: TrlMapNodeDef[];
  config: TrlTreasureConfig;
  /** Replace every node's assignment (nodeId → itemIds). Nodes absent = cleared. */
  onAssignmentsChange: (assignments: Record<string, string[]>) => void;
  disabled?: boolean;
}

export function TreasureAssignmentPanel({
  nodes,
  config,
  onAssignmentsChange,
  disabled = false,
}: TreasureAssignmentPanelProps) {
  // Item rewards are shown with their platform icon, so a long list of
  // assignments is scannable by picture rather than by reading every name.
  const itemSummaries = usePlatformItemSummaries(
    config.items.flatMap((i) => (i.kind === 'cartel' && i.itemId ? [i.itemId] : [])),
  );

  // Group by derived round; level 0 (unconnected) is called out separately
  // because those planets can never be reached and so never hold treasure.
  const rounds = useMemo(() => {
    const byLevel = new Map<number, TrlMapNodeDef[]>();
    for (const n of nodes) {
      const level = n.derivedLevel ?? 0;
      if (!byLevel.has(level)) byLevel.set(level, []);
      byLevel.get(level)!.push(n);
    }
    return [...byLevel.entries()].sort((a, b) => a[0] - b[0]);
  }, [nodes]);

  /** Current assignments, as the flat map the change handler expects. */
  const currentAssignments = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const n of nodes) {
      if (n.treasureItemIds && n.treasureItemIds.length > 0) out[n.id] = [...n.treasureItemIds];
    }
    return out;
  }, [nodes]);

  const itemsById = useMemo(
    () => new Map(config.items.map((i) => [i.id, i])),
    [config.items],
  );

  const placedCount = Object.values(currentAssignments).reduce((sum, ids) => sum + ids.length, 0);
  const totalPlacements = config.items.reduce((sum, it) => sum + Math.max(1, it.quantity), 0);
  const hasEligibleNodes = nodes.some((n) => (n.derivedLevel ?? 0) >= 1);

  const reroll = () => {
    onAssignmentsChange(distributeTrlTreasure(config.items, nodes, config.tierSpread));
  };

  const clearAll = () => onAssignmentsChange({});

  const removeFromNode = (nodeId: string, index: number) => {
    const next = { ...currentAssignments };
    const ids = [...(next[nodeId] ?? [])];
    ids.splice(index, 1);
    if (ids.length > 0) next[nodeId] = ids;
    else delete next[nodeId];
    onAssignmentsChange(next);
  };

  const addToNode = (nodeId: string, itemId: string) => {
    if (!itemId) return;
    onAssignmentsChange({
      ...currentAssignments,
      [nodeId]: [...(currentAssignments[nodeId] ?? []), itemId],
    });
  };

  return (
    <div className="d-flex flex-column gap-xs">
      <span className="fs-xs text-uppercase text-slate">Distribution</span>
      <span className="fs-xs text-slate">
        Where each reward currently sits. Re-roll to redistribute the whole pool, or adjust a
        planet by hand. Locks with the map when the game reaches the lobby.
      </span>

      <div className="d-flex gap-xs align-items-center flex-wrap">
        <button
          type="button"
          className="btn btn--sm"
          onClick={reroll}
          disabled={disabled || config.items.length === 0 || !hasEligibleNodes}
          title={
            config.items.length === 0
              ? 'Add treasure to the list first'
              : !hasEligibleNodes
                ? 'Connect the map so planets have rounds'
                : 'Randomly redistribute every reward'
          }
        >
          🎲 Re-roll distribution
        </button>
        <button
          type="button"
          className="btn btn--outline btn--slate btn--sm"
          onClick={clearAll}
          disabled={disabled || placedCount === 0}
        >
          Clear all
        </button>
        <span className="fs-xs text-slate">
          {placedCount} of {totalPlacements} placed
        </span>
      </div>

      {placedCount !== totalPlacements && totalPlacements > 0 && (
        <span className="fs-xs text-amber">
          The pool and the map are out of sync — re-roll to place every reward.
        </span>
      )}

      {rounds.length === 0 ? (
        <p className="text-slate fs-sm m-0" style={{ padding: '1rem 0' }}>
          No planets on the map yet.
        </p>
      ) : (
        rounds.map(([level, levelNodes]) => (
          <div key={level} className="d-flex flex-column gap-2xs">
            <strong className="fs-sm">
              {level === 0 ? 'Unconnected' : `Round ${level}`}
              <span className="text-slate fs-xs"> — {levelNodes.length} planet{levelNodes.length === 1 ? '' : 's'}</span>
            </strong>

            {level === 0 && (
              <span className="fs-xs text-slate">
                Unconnected planets have no round and are skipped by the roll.
              </span>
            )}

            {levelNodes.map((node) => {
              const assigned = currentAssignments[node.id] ?? [];
              return (
                <div
                  key={node.id}
                  className="border-md border-blue-light rounded-sm p-2xs d-flex flex-column gap-2xs"
                >
                  <div className="d-flex align-items-center gap-xs justify-content-between">
                    <span className="fs-sm">{node.label}</span>
                    {config.items.length > 0 && level > 0 && (
                      <select
                        className="input input--sm"
                        style={{ maxWidth: '12rem' }}
                        value=""
                        onChange={(e) => { addToNode(node.id, e.target.value); e.target.value = ''; }}
                        disabled={disabled}
                        aria-label={`Add treasure to ${node.label}`}
                      >
                        <option value="">+ Add treasure…</option>
                        {config.items.map((it) => (
                          <option key={it.id} value={it.id}>
                            {describeTrlTreasureItem(it)} ({TRL_TREASURE_TIER_LABELS[it.tier]})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {assigned.length === 0 ? (
                    <span className="fs-xs text-slate">No treasure</span>
                  ) : (
                    <div className="d-flex flex-column gap-2xs">
                      {assigned.map((itemId, index) => {
                        const item = itemsById.get(itemId);
                        return (
                          <div
                            key={`${itemId}-${index}`}
                            className="d-flex align-items-center justify-content-between gap-xs"
                          >
                            <span className="fs-xs d-flex align-items-center gap-2xs">
                              {item ? (
                                <>
                                  {item.kind === 'cartel' ? (
                                    <ItemIcon
                                      iconUrl={item.itemId ? itemSummaries.get(item.itemId)?.icon?.url : undefined}
                                      name={item.cartelItemName ?? 'SWTOR item'}
                                      size={20}
                                    />
                                  ) : (
                                    <span aria-hidden="true">💎</span>
                                  )}
                                  {describeTrlTreasureItem(item)}
                                  <span className="text-slate">· {TRL_TREASURE_TIER_LABELS[item.tier]}</span>
                                </>
                              ) : (
                                // Only reachable transiently: saving prunes these.
                                <span className="text-amber">Removed reward</span>
                              )}
                            </span>
                            <button
                              type="button"
                              className="btn btn--outline btn--slate btn--sm"
                              onClick={() => removeFromNode(node.id, index)}
                              disabled={disabled}
                              aria-label="Remove this treasure"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}
